import {
  getPizzeriaDefaultPriceLabels,
  getPizzeriaDefaultSubsection,
  PIZZERIA_MENU_STRUCTURE
} from "../../lib/pizzeria-menu-schema";

export const SECTION_OPTIONS = PIZZERIA_MENU_STRUCTURE.map((section) => ({
  value: section.section_key,
  label: section.title
}));

const VALID_SECTION_KEYS = new Set(SECTION_OPTIONS.map((section) => section.value));

export function buildDraftForSection(sectionKey) {
  const defaultLabels = getPizzeriaDefaultPriceLabels(sectionKey);
  return {
    section_key: sectionKey,
    subsection_key: getPizzeriaDefaultSubsection(sectionKey),
    name: "",
    description: "",
    price1_label: defaultLabels[0] || "Prezzo",
    price1_amount_cents: "",
    price2_label: defaultLabels[1] || "",
    price2_amount_cents: "",
    sort_order: "100",
    active: true
  };
}

export function normalizePizzeriaMenuApiItem(rawItem) {
  if (!rawItem || typeof rawItem !== "object" || Array.isArray(rawItem)) {
    return null;
  }

  const source = rawItem;
  const sectionKey = typeof source.section_key === "string" ? source.section_key : "";
  const name = typeof source.name === "string" ? source.name.trim() : "";
  const prices = Array.isArray(source.prices) ? source.prices : [];

  if (!sectionKey || !VALID_SECTION_KEYS.has(sectionKey) || !name || prices.length === 0) {
    return null;
  }

  return {
    id: typeof source.id === "string" ? source.id : "",
    section_key: sectionKey,
    subsection_key: typeof source.subsection_key === "string" ? source.subsection_key : "",
    name,
    description: typeof source.description === "string" ? source.description : "",
    prices,
    sort_order: Number.isInteger(Number(source.sort_order)) ? Number(source.sort_order) : 100,
    active: source.active !== false,
    updated_at: typeof source.updated_at === "string" ? source.updated_at : ""
  };
}

export function toPizzeriaMenuDraft(item) {
  const defaultLabels = getPizzeriaDefaultPriceLabels(item.section_key);
  const firstPrice = item.prices?.[0];
  const secondPrice = item.prices?.[1];

  return {
    section_key: item.section_key,
    subsection_key: item.subsection_key || getPizzeriaDefaultSubsection(item.section_key),
    name: item.name || "",
    description: item.description || "",
    price1_label:
      typeof firstPrice?.label === "string" && firstPrice.label.trim()
        ? firstPrice.label
        : defaultLabels[0] || "Prezzo",
    price1_amount_cents:
      firstPrice && Number.isInteger(Number(firstPrice.amount_cents))
        ? String(firstPrice.amount_cents)
        : "",
    price2_label:
      typeof secondPrice?.label === "string" && secondPrice.label.trim()
        ? secondPrice.label
        : defaultLabels[1] || "",
    price2_amount_cents:
      secondPrice && Number.isInteger(Number(secondPrice.amount_cents))
        ? String(secondPrice.amount_cents)
        : "",
    sort_order: String(item.sort_order ?? 100),
    active: Boolean(item.active)
  };
}

export function toPizzeriaMenuPayload(draft) {
  const name = typeof draft.name === "string" ? draft.name.trim() : "";
  if (!name) {
    throw new Error("Il nome voce e obbligatorio.");
  }

  const firstLabel = typeof draft.price1_label === "string" ? draft.price1_label.trim() : "";
  const firstAmountRaw =
    draft.price1_amount_cents === null || draft.price1_amount_cents === undefined
      ? ""
      : String(draft.price1_amount_cents).trim();

  if (!firstLabel || !firstAmountRaw) {
    throw new Error("Il primo prezzo richiede etichetta e importo.");
  }

  const firstAmount = Number(firstAmountRaw);
  if (!Number.isInteger(firstAmount) || firstAmount < 1) {
    throw new Error("Il primo prezzo deve essere un intero positivo in centesimi.");
  }

  const secondLabel = typeof draft.price2_label === "string" ? draft.price2_label.trim() : "";
  const secondAmountRaw =
    draft.price2_amount_cents === null || draft.price2_amount_cents === undefined
      ? ""
      : String(draft.price2_amount_cents).trim();
  const hasSecondLabel = Boolean(secondLabel);
  const hasSecondAmount = Boolean(secondAmountRaw);

  if (hasSecondLabel !== hasSecondAmount) {
    throw new Error("Compila sia etichetta che importo del secondo prezzo, oppure lascia entrambi vuoti.");
  }

  const prices = [
    {
      label: firstLabel,
      amount_cents: firstAmount
    }
  ];

  if (hasSecondLabel && hasSecondAmount) {
    const secondAmount = Number(secondAmountRaw);
    if (!Number.isInteger(secondAmount) || secondAmount < 1) {
      throw new Error("Il secondo prezzo deve essere un intero positivo in centesimi.");
    }

    prices.push({
      label: secondLabel,
      amount_cents: secondAmount
    });
  }

  const sectionKey = typeof draft.section_key === "string" ? draft.section_key : "";
  const subsectionKey =
    typeof draft.subsection_key === "string" ? draft.subsection_key.trim() : "";
  const sortOrder = Number(draft.sort_order);

  if (!Number.isInteger(sortOrder)) {
    throw new Error("L'ordine deve essere un numero intero.");
  }

  if (sectionKey === "PIZZE" && !subsectionKey) {
    throw new Error("Per la sezione PIZZE la sottosezione e obbligatoria.");
  }

  return {
    section_key: sectionKey,
    subsection_key: sectionKey === "PIZZE" ? subsectionKey : null,
    name,
    description: typeof draft.description === "string" ? draft.description.trim() : "",
    prices,
    sort_order: sortOrder,
    active: Boolean(draft.active)
  };
}
